<template>
	<nav
		:class="{
			collapsed: menuCollapsed,
			isAccount: $root.sessionStore.isAccountUser || $root.appStore.appType === 'marketplace',
		}"
	>
		<!-- menuCollapsed -->
		<p-tiered-menu v-if="menuCollapsed" :model="formatted_navigation">
			<template #item="{ item, props, hasSubmenu }">
				<router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route">
					<a :href="href" v-bind="props.action" @click="navigate">
						<alt-icon
							v-tooltip.right="{
								value: `${item.label}`,
							}"
							:type="item.icon"
						/>
						<span v-if="item.depth !== 1">{{ item.label }}</span>
						<span v-if="item.depth !== 1 && item.items" class="pi ml-auto pi-angle-right" />
					</a>
				</router-link>
				<a v-else :href="item.url" :target="item.target" v-bind="props.action">
					<alt-icon
						v-tooltip.right="{
							value: `${item.label} ›`,
						}"
						:type="item.icon"
					/>
					<span v-if="item.depth !== 1">{{ item.label }}</span>
					<span v-if="item.depth !== 1 && item.items" class="pi mr-4 ml-auto pi-angle-right" />
				</a>
			</template>
		</p-tiered-menu>
		<!-- else !menuCollapsed -->
		<p-panel-menu v-else :model="formatted_navigation">
			<template #item="{ item, active }">
				<router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
					<a class="flex align-items-center cursor-pointer" :href="href" @click="navigate">
						<alt-icon :type="item.icon" />
						<span class="ml-2">{{ item.label }}</span>
					</a>
				</router-link>
				<a v-else class="flex align-items-center cursor-pointer" :href="item.url" :target="item.target">
					<alt-icon :type="item.icon" />
					<span class="ml-2">{{ item.label }}</span>
					<span v-if="item.items" class="pi ml-auto" :class="active ? 'pi-angle-down' : 'pi-angle-right'" />
				</a>
			</template>
		</p-panel-menu>
	</nav>
</template>

<script lang="ts">
import pPanelMenu from 'primevue/panelmenu';
import pTieredMenu from 'primevue/tieredmenu';
import { find, set } from 'lodash-es';

export default {
	name: 'SidebarMenu',
	components: {
		pPanelMenu,
		pTieredMenu,
	},
	props: {
		menuCollapsed: Boolean,
		mpid: String,
	},
	data() {
		return {
			current_menu: null,
			has_hovered: false,
		};
	},
	computed: {
		navigation() {
			return this.$root.sessionStore.getNavigation(this.mpid);
		},
		formatted_navigation() {
			let formatted_navigation = [];
			function addDepth(items, depth = 1) {
				return items.map((item) => {
					const newItem = { ...item, depth };
					if (item.items && Array.isArray(item.items)) {
						newItem.items = addDepth(item.items, depth + 1);
					}
					return newItem;
				});
			}

			if (this.navigation.length > 0) {
				// Add depth to all navigation items for sub-menus
				formatted_navigation = addDepth(this.navigation);
			}

			// Add Marketplaces to the nav for the item "Marketplaces" as a child item if it exists and they have the permissions
			const marketplace_options = this.$root.appStore.options.marketplaces;
			const marketplace_option = find(formatted_navigation, (item) => item.label === 'Marketplaces');
			if (marketplace_option) {
				const marketplace_items = marketplace_option.items || [];
				marketplace_options.forEach((marketplace) => {
					const marketplace_item = {
						label: marketplace.value,
						route: `/marketplaces/manage/${marketplace.value}/accounts`,
					};
					marketplace_items.unshift(marketplace_item);
				});
				set(marketplace_option, 'items', marketplace_items);
			}

			// Prefix all routes with the mpid if it exists for marketplace navigation
			formatted_navigation.forEach((item) => {
				if (item.route) {
					item.route = this.prefixRoute(item.route);
				}
				if (item.items && Array.isArray(item.items)) {
					item.items.forEach((subItem) => {
						if (subItem.route) {
							subItem.route = this.prefixRoute(subItem.route);
						}
					});
				}
			});

			// Add filter out items that the user does not have permission to see
			formatted_navigation = formatted_navigation.filter((item) => {
				if (this.$root.sessionStore.hasPermission([`SidebarMenu.${item.permissions}`])) {
					if (item.items && Array.isArray(item.items)) {
						item.items = item.items.filter((subItem) =>
							this.$root.sessionStore.hasPermission([`SidebarMenu.${subItem.permissions}`])
						);
					}
					return true;
				}
				return false;
			});

			return formatted_navigation;
		},
	},
	methods: {
		prefixRoute(route) {
			if (this.mpid) {
				return `/marketplaces/manage/${this.mpid}` + route;
			}
			return route;
		},
	},
};
</script>

<style scoped lang="less">
@import (reference) '@/styles/responsive';

a {
	&:hover {
		text-decoration: none;
	}
}

nav.collapsed {
	overflow: visible !important;
	margin-top: 6px;
}

#marketplace-wrapper nav,
nav.isAccount {
	:deep(.p-tieredmenu .p-submenu-list) {
		border: 1px solid var(--gray-15);
		// border-left: 0;
		background: var(--white);
		.p-menuitem > .p-menuitem-content {
			background: var(--white);
		}
	}
	:deep(.p-tieredmenu .p-menuitem-content) {
		background: var(--white);
		.p-highlight {
			background: var(--gray-15);
		}
		&:hover {
			background: var(--white);
			a {
				color: var(--color-b);
				background: var(--white);
			}
		}
		a {
			color: var(--color-a);
			background-color: var(--white);
			&:hover {
				text-decoration: none;
				background: transparent;
			}
		}
	}
	:deep(.p-tieredmenu .p-submenu-list .p-menuitem-content) {
		background: var(--white);
		&:hover {
			background: var(--gray-05);
			color: var(--color-a);
			a {
				color: var(--color-b);
				background: var(--gray-05);
			}
		}
		a {
			color: var(--color-a);
			&:hover {
				background: var(--gray-05);
				color: var(--color-b);
			}
		}
	}
	:deep(.p-panelmenu .p-panelmenu-panel .p-panelmenu-header .p-panelmenu-header-content) {
		background: var(--white);
		&:hover {
			background: var(--gray-05);
			color: var(--color-a);
		}
		a {
			.material-symbols-outlined {
				color: var(--color-a);
			}
			color: var(--gray-50);
			&:hover {
				background: var(--gray-05);
				color: var(--color-a);
			}
		}
	}
	:deep(.p-submenu-list) {
		background: var(--white);
		.p-menuitem .p-menuitem-content {
			&:hover {
				background: var(--gray-05);
				color: var(--color-a);
			}
		}
		a {
			&:hover {
				background: var(--gray-05);
				color: var(--color-a);
			}
			color: var(--gray-50);
		}
	}
	:deep(
		.p-panelmenu .p-panelmenu-content .p-menuitem:not(.p-highlight):not(.p-disabled).p-focus > .p-menuitem-content
	) {
		background: var(--white);
		a {
			color: var(--color-a);
		}
	}
	:deep(.p-panelmenu .p-panelmenu-content .p-menuitem:not(.p-highlight):not(.p-disabled) > .p-menuitem-content:hover) {
		background: var(--gray-05);
	}
}
</style>
